// pages/AdminPage.jsx — Admin panel, fully i18n + mobile responsive
import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Badge,
  Input,
  Modal,
  StatCard,
  Logo,
  Icons,
} from "../components/UI";
import ImageUploader from "../components/ImageUploader";
import { useLang } from "../App";
import { useAsync } from "../hooks";
import {
  authService,
  productService,
  categoryService,
  inquiryService,
  uploadService,
  statsService,
} from "../services/api";

// ─── Shared helpers ───────────────────────────────────────────────────────────
const TabErr = ({ msg, onRetry }) => (
  <div
    style={{
      background: "#fff3f3",
      border: "1px solid #fca5a5",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 20,
    }}
  >
    <span>⚠️</span>
    <p style={{ flex: 1, fontSize: 13, color: "#991b1b" }}>{msg}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          background: "#dc2626",
          color: "#fff",
          border: "none",
          borderRadius: 7,
          padding: "5px 12px",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: "var(--sans)",
        }}
      >
        Retry
      </button>
    )}
  </div>
);

const RowSkel = ({ cols = 6, rows = 4 }) => (
  <>
    {[...Array(rows)].map((_, i) => (
      <tr key={i}>
        {[...Array(cols)].map((_, j) => (
          <td key={j} style={{ padding: "13px 16px" }}>
            <div
              style={{
                height: 13,
                borderRadius: 6,
                background:
                  "linear-gradient(90deg,var(--foam) 25%,var(--mist) 50%,var(--foam) 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s infinite",
                width: j === 0 ? "80%" : "55%",
              }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ tab, setTab, onLogout, onViewSite, open, setOpen }) => {
  const { t } = useLang();
  const ITEMS = [
    { k: "dashboard", label: t("dashboard"), icon: <Icons.Grid size={15} /> },
    {
      k: "products",
      label: t("manageProducts"),
      icon: <Icons.Leaf size={15} />,
    },
    { k: "categories", label: t("manageCategories"), icon: "🌱" },
    {
      k: "inquiries",
      label: t("manageInquiries"),
      icon: <Icons.Inbox size={15} />,
    },
  ];

  const content = (
    <>
      {/* Logo header */}
      <div
        style={{
          padding: "20px",
          borderBottom: "1px solid rgba(0,0,0,.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Logo />
        {/* Close button — only on mobile */}
        <button
          onClick={() => setOpen(false)}
          style={{
            display: "flex",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--stone)",
            padding: 4,
          }}
          className="sidebar-close"
        >
          <Icons.Close size={18} />
        </button>
      </div>
      <div
        style={{
          marginTop: 4,
          padding: "0 20px 10px",
          fontSize: 11,
          color: "var(--stone)",
          fontWeight: 500,
        }}
      >
        Admin Dashboard
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: "6px 10px", overflowY: "auto" }}>
        {ITEMS.map((item) => {
          const active = tab === item.k;
          return (
            <button
              key={item.k}
              onClick={() => {
                setTab(item.k);
                setOpen(false);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "11px 14px",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                fontFamily: "var(--sans)",
                fontWeight: active ? 600 : 400,
                fontSize: 14,
                marginBottom: 2,
                background: active ? "var(--foam)" : "transparent",
                color: active ? "var(--pine)" : "var(--stone)",
                transition: "all .2s",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "var(--snow)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              {item.icon} {item.label}
              {active && (
                <span
                  style={{
                    marginLeft: "auto",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--emerald)",
                    flexShrink: 0,
                  }}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer buttons */}
      <div
        style={{
          padding: "10px 10px 16px",
          borderTop: "1px solid rgba(0,0,0,.06)",
        }}
      >
        <button
          onClick={onViewSite}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "10px 14px",
            border: "none",
            borderRadius: 10,
            cursor: "pointer",
            background: "transparent",
            color: "var(--stone)",
            fontFamily: "var(--sans)",
            fontSize: 13,
            fontWeight: 500,
            marginBottom: 6,
            transition: "all .2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--snow)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          ← {t("home") || "View Website"}
        </button>
        <button
          onClick={onLogout}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "10px 14px",
            border: "none",
            borderRadius: 10,
            cursor: "pointer",
            background: "#fee2e2",
            color: "#991b1b",
            fontFamily: "var(--sans)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Icons.Logout size={14} /> {t("logout") || "Logout"}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — fixed, always visible */}
      <aside
        className="admin-sidebar-desktop"
        style={{
          width: 240,
          background: "#fff",
          borderRight: "1px solid rgba(0,0,0,.07)",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          bottom: 0,
          left: 0,
          zIndex: 100,
        }}
      >
        {content}
      </aside>

      {/* Mobile sidebar — slide in drawer */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 998,
          background: "rgba(0,0,0,.45)",
          backdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .3s",
        }}
        className="admin-sidebar-backdrop"
      />
      <aside
        className="admin-sidebar-mobile"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 999,
          width: 260,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          boxShadow: "20px 0 60px rgba(0,0,0,.15)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform .32s var(--ease)",
        }}
      >
        {content}
      </aside>

      {/* Inline CSS for admin responsive */}
      <style>{`
        @media (min-width: 769px) {
          .admin-sidebar-mobile   { display: none !important; }
          .admin-sidebar-backdrop { display: none !important; }
          .sidebar-close          { display: none !important; }
          .admin-hamburger        { display: none !important; }
          .admin-main             { margin-left: 240px !important; }
        }
        @media (max-width: 768px) {
          .admin-sidebar-desktop  { display: none !important; }
          .admin-main             { margin-left: 0 !important; }
        }
      `}</style>
    </>
  );
};

// ─── Dashboard Tab ────────────────────────────────────────────────────────────
const DashboardTab = ({ onInquiries }) => {
  const { t } = useLang();
  const [counts, setCounts] = useState(null); // from statsService.getAdmin()
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Fetch stats + recent inquiries in parallel
    Promise.all([
      statsService.getAdmin().catch(() => null),
      inquiryService.getAll().catch(() => []),
    ])
      .then(([stats, inqs]) => {
        if (stats) setCounts(stats);
        setInquiries(inqs.slice(0, 5));
      })
      .finally(() => setLoading(false));
  }, []);

  // All values are live from DB via /api/Stats/admin
  const STATS = [
    {
      icon: "🌱",
      val: loading ? "…" : (counts?.products.total ?? 0),
      label: t("totalProducts"),
      change: counts
        ? `+${counts.products.addedThisWeek} ${t("thisWeek") || "this week"}`
        : "",
      trend: "up",
    },
    {
      icon: "📂",
      val: loading ? "…" : (counts?.categories.total ?? 0),
      label: t("totalCats"),
      change: "",
      trend: null,
    },
    {
      icon: "📋",
      val: loading ? "…" : (counts?.inquiries.newCount ?? 0),
      label: t("pendingInq"),
      change: counts
        ? `+${counts.inquiries.thisWeek} ${t("thisWeek") || "this week"}`
        : "",
      trend: "up",
    },
    {
      icon: "👨‍🌾",
      val: loading
        ? "…"
        : counts
          ? `${counts.company.happyFarmers.toLocaleString()}+`
          : "5,000+",
      label: t("happyFarmers"),
      change: t("growing") || "Growing",
      trend: "up",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2
          style={{
            fontFamily: "var(--serif)",
            fontSize: 28,
            color: "var(--ink)",
            marginBottom: 4,
          }}
        >
          {t("goodMorning") || "Good morning! 👋"}
        </h2>
        <p style={{ color: "var(--stone)", fontSize: 15 }}>
          {t("happeningToday") || "Here's what's happening today."}
        </p>
      </div>

      {/* Stat cards — responsive grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(175px,1fr))",
          gap: 16,
          marginBottom: 28,
        }}
      >
        {STATS.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* Recent inquiries */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid rgba(0,0,0,.07)",
          overflow: "hidden",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            padding: "16px 22px",
            borderBottom: "1px solid var(--foam)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>
            {t("recentInquiries") || "Recent Inquiries"}
          </h3>
          <button
            onClick={onInquiries}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "var(--emerald)",
              fontWeight: 600,
              fontFamily: "var(--sans)",
            }}
          >
            {t("viewAll") || "View all"} →
          </button>
        </div>
        {inquiries.length === 0 && !loading && (
          <p
            style={{
              padding: "20px 22px",
              color: "var(--stone)",
              fontSize: 14,
            }}
          >
            No inquiries yet.
          </p>
        )}
        {inquiries.map((inq, i) => (
          <div
            key={inq.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "13px 22px",
              borderBottom:
                i < inquiries.length - 1 ? "1px solid var(--foam)" : "none",
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "linear-gradient(135deg,var(--sage),var(--pine))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--serif)",
                fontSize: 14,
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {inq.name.charAt(0)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}
              >
                {inq.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--stone)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {inq.msg}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              {inq.date && (
                <span style={{ fontSize: 11, color: "var(--stone)" }}>
                  {inq.date}
                </span>
              )}
              <Badge color={inq.status === "New" ? "green" : "amber"} dot>
                {inq.status}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Products Tab ─────────────────────────────────────────────────────────────
const ProductsTab = () => {
  const { lang, t } = useLang();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const EMPTY = {
    name: { en: "", hi: "", gu: "" },
    price: "",
    stock: "In Stock",
    cat: "",
    isFeatured: false,
    imageUrl: null,
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([productService.getAll(), categoryService.getAll()])
      .then(([p, c]) => {
        setProducts(p);
        setCats(c);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing({ ...EMPTY, cat: cats[0]?.slug || "" });
    setFormOpen(true);
  };
  const openEdit = (p) => {
    setEditing({
      id: p.id,
      name: {
        en: p.name?.en || "",
        hi: p.name?.hi || "",
        gu: p.name?.gu || "",
      },
      price: p.price || "",
      stock: p.stock || "In Stock",
      cat: p.cat || cats[0]?.slug || "",
      isFeatured: p.featured || false,
      imageUrl: p.imageUrl || null,
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        nameEn: editing.name.en,
        nameHi: editing.name.hi,
        nameGu: editing.name.gu,
        price: Number(editing.price),
        stockStatus: editing.stock,
        categorySlug: editing.cat,
        isFeatured: Boolean(editing.isFeatured),
        imageUrl: editing.imageUrl || null,
      };
      if (editing.id) {
        const res = await productService.update(editing.id, payload);
        const updated =
          res && res.id
            ? res
            : {
                ...products.find((p) => p.id === editing.id),
                price: Number(editing.price),
                stock: editing.stock,
                cat: editing.cat,
                featured: editing.isFeatured,
                imageUrl: editing.imageUrl,
                name: {
                  en: editing.name.en,
                  hi: editing.name.hi,
                  gu: editing.name.gu,
                },
              };
        setProducts((prev) =>
          prev.map((p) => (p.id === editing.id ? updated : p)),
        );
      } else {
        const res = await productService.create(payload);
        const created =
          res && res.id
            ? res
            : {
                ...payload,
                id: Date.now(),
                emoji: "🌱",
                bg: "#e8f5e9",
                name: {
                  en: payload.nameEn,
                  hi: payload.nameHi,
                  gu: payload.nameGu,
                },
                stock: payload.stockStatus,
                cat: payload.categorySlug,
                featured: payload.isFeatured,
              };
        setProducts((prev) => [...prev, created]);
      }
      setFormOpen(false);
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await productService.remove(delId);
      setProducts((prev) => prev.filter((p) => p.id !== delId));
      setDelId(null);
    } catch (e) {
      alert("Delete failed: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "var(--serif)",
              fontSize: 26,
              color: "var(--ink)",
            }}
          >
            {t("manageProducts")}
          </h2>
          <p style={{ color: "var(--stone)", fontSize: 13, marginTop: 2 }}>
            {products.length} {t("products") || "products"}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<Icons.Plus size={14} />}
          onClick={openAdd}
        >
          {t("addProduct")}
        </Button>
      </div>

      {error && <TabErr msg={error} onRetry={load} />}

      {/* Table — scrollable on mobile */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid rgba(0,0,0,.07)",
          overflow: "hidden",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  background: "var(--snow)",
                  borderBottom: "1px solid var(--foam)",
                }}
              >
                {[
                  "Product",
                  "Category",
                  "Price",
                  "Status",
                  "Featured",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      color: "var(--stone)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <RowSkel cols={6} rows={4} />
              ) : (
                products.map((p) => (
                  <tr
                    key={p.id}
                    style={{
                      borderBottom: "1px solid var(--foam)",
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--snow)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td style={{ padding: "13px 16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 11,
                        }}
                      >
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 9,
                            background: p.bg || "var(--foam)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 21,
                            flexShrink: 0,
                            overflow: "hidden",
                          }}
                        >
                          {p.imageUrl ? (
                            <img
                              src={uploadService.getUrl(p.imageUrl)}
                              alt={p.name.en}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            p.emoji || "🌱"
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: "var(--ink)",
                            }}
                          >
                            {p.name[lang] || p.name.en}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--stone)" }}>
                            DBK-{String(p.id).padStart(3, "0")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "var(--stone)",
                      }}
                    >
                      {cats.find((c) => c.slug === p.cat)?.name[lang] ||
                        cats.find((c) => c.slug === p.cat)?.name.en ||
                        p.cat ||
                        "—"}
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontFamily: "var(--serif)",
                        fontSize: 17,
                        color: "var(--pine)",
                      }}
                    >
                      ₹{p.price}
                    </td>
                    <td style={{ padding: "13px 16px" }}>
                      <Badge
                        color={
                          p.stock === "In Stock"
                            ? "green"
                            : p.stock === "Limited"
                              ? "amber"
                              : "red"
                        }
                        dot
                      >
                        {p.stock === "In Stock"
                          ? t("inStock")
                          : p.stock === "Limited"
                            ? t("limited")
                            : t("outOfStock")}
                      </Badge>
                    </td>
                    <td style={{ padding: "13px 16px" }}>
                      {p.featured ? (
                        <Badge color="amber">★ Yes</Badge>
                      ) : (
                        <span style={{ fontSize: 12, color: "var(--stone)" }}>
                          —
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "13px 16px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => openEdit(p)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            background: "var(--foam)",
                            border: "none",
                            borderRadius: 7,
                            padding: "5px 9px",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "var(--pine)",
                            fontFamily: "var(--sans)",
                            transition: "all .2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "var(--mint)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "var(--foam)")
                          }
                        >
                          <Icons.Edit size={12} /> {t("update") || "Edit"}
                        </button>
                        <button
                          onClick={() => setDelId(p.id)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background: "#fee2e2",
                            border: "none",
                            borderRadius: 7,
                            padding: "5px 8px",
                            cursor: "pointer",
                            color: "#991b1b",
                            transition: "all .2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#fca5a5")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "#fee2e2")
                          }
                        >
                          <Icons.Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing?.id ? t("editProduct") : t("addProduct")}
        footer={
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              variant="primary"
              size="md"
              loading={saving}
              style={{ flex: 1, justifyContent: "center" }}
              onClick={save}
            >
              💾 {t("save")}
            </Button>
            <Button
              variant="ghost"
              size="md"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setFormOpen(false)}
            >
              {t("cancel")}
            </Button>
          </div>
        }
      >
        {editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <ImageUploader
              currentImage={editing.imageUrl}
              prefix="product"
              label="Product Image"
              height={180}
              onUploaded={(path) =>
                setEditing((p) => ({ ...p, imageUrl: path }))
              }
            />
            <Input
              label="Name (EN)"
              name="n_en"
              value={editing.name.en}
              required
              onChange={(e) =>
                setEditing((p) => ({
                  ...p,
                  name: { ...p.name, en: e.target.value },
                }))
              }
            />
            <Input
              label="Name (हि)"
              name="n_hi"
              value={editing.name.hi}
              onChange={(e) =>
                setEditing((p) => ({
                  ...p,
                  name: { ...p.name, hi: e.target.value },
                }))
              }
            />
            <Input
              label="Name (ગુ)"
              name="n_gu"
              value={editing.name.gu}
              onChange={(e) =>
                setEditing((p) => ({
                  ...p,
                  name: { ...p.name, gu: e.target.value },
                }))
              }
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Input
                label="Price (₹)"
                name="price"
                type="number"
                required
                value={editing.price}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, price: e.target.value }))
                }
              />
              <Input
                as="select"
                label="Stock Status"
                name="stock"
                value={editing.stock}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, stock: e.target.value }))
                }
              >
                <option value="In Stock">{t("inStock")}</option>
                <option value="Limited">{t("limited")}</option>
                <option value="Out of Stock">{t("outOfStock")}</option>
              </Input>
            </div>
            {cats.length > 0 && (
              <Input
                as="select"
                label={t("category") || "Category"}
                name="cat"
                value={editing.cat}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, cat: e.target.value }))
                }
              >
                {cats.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name[lang] || c.name.en}
                  </option>
                ))}
              </Input>
            )}
            {/* Featured toggle */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div
                onClick={() =>
                  setEditing((p) => ({ ...p, isFeatured: !p.isFeatured }))
                }
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  background: editing.isFeatured
                    ? "var(--emerald)"
                    : "var(--mist)",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background .2s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: editing.isFeatured ? 23 : 3,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "white",
                    boxShadow: "0 1px 4px rgba(0,0,0,.2)",
                    transition: "left .2s",
                  }}
                />
              </div>
              <span
                style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}
              >
                Featured Product
              </span>
            </label>
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={Boolean(delId)}
        onClose={() => setDelId(null)}
        title={t("deleteProduct") || "Delete Product"}
        subtitle="This action cannot be undone."
        size="sm"
        footer={
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              variant="danger"
              size="md"
              loading={deleting}
              style={{ flex: 1, justifyContent: "center" }}
              onClick={del}
            >
              {t("yesDelete") || "Yes, Delete"}
            </Button>
            <Button
              variant="ghost"
              size="md"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setDelId(null)}
            >
              {t("cancel")}
            </Button>
          </div>
        }
      >
        <p style={{ color: "var(--stone)", fontSize: 15 }}>
          Are you sure you want to delete{" "}
          <strong style={{ color: "var(--ink)" }}>
            {products.find((p) => p.id === delId)?.name.en}
          </strong>
          ?
        </p>
      </Modal>
    </div>
  );
};

// ─── Categories Tab ───────────────────────────────────────────────────────────
const CategoriesTab = () => {
  const { lang, t } = useLang();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [delId, setDelId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const EMOJI_LIST = [
    "🥦",
    "🍉",
    "🌸",
    "🌾",
    "🌿",
    "🍅",
    "🍆",
    "🫑",
    "🥬",
    "🌼",
    "🥕",
    "🌽",
    "🌱",
    "🫘",
    "🥜",
  ];

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    categoryService
      .getAll()
      .then(setCats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing({
      icon: "🥦",
      nameEn: "",
      nameHi: "",
      nameGu: "",
      imageUrl: null,
    });
    setSaveErr("");
    setFormOpen(true);
  };
  const openEdit = (c) => {
    setEditing({
      id: c.id,
      icon: c.icon || "🥦",
      nameEn: c.name?.en || "",
      nameHi: c.name?.hi || "",
      nameGu: c.name?.gu || "",
      imageUrl: c.imageUrl || null,
      _orig: c,
    });
    setSaveErr("");
    setFormOpen(true);
  };

  const save = async () => {
    if (!editing.nameEn.trim()) {
      setSaveErr("English name is required.");
      return;
    }
    setSaveErr("");
    setSaving(true);
    try {
      const payload = {
        nameEn: editing.nameEn.trim(),
        nameHi: editing.nameHi.trim(),
        nameGu: editing.nameGu.trim(),
        icon: editing.icon || "🥦",
        imageUrl: editing.imageUrl || null,
      };
      if (editing.id) {
        const res = await categoryService.update(editing.id, payload);
        const updated =
          res && res.id
            ? res
            : {
                ...editing._orig,
                icon: payload.icon,
                imageUrl: payload.imageUrl,
                name: {
                  en: payload.nameEn,
                  hi: payload.nameHi,
                  gu: payload.nameGu,
                },
              };
        setCats((prev) => prev.map((c) => (c.id === editing.id ? updated : c)));
      } else {
        const res = await categoryService.create(payload);
        const created =
          res && res.id
            ? res
            : {
                id: Date.now(),
                slug: payload.nameEn.toLowerCase().replace(/\s+/g, "-"),
                icon: payload.icon,
                imageUrl: payload.imageUrl,
                count: 0,
                from: "#1a5c2e",
                to: "#2d8a45",
                name: {
                  en: payload.nameEn,
                  hi: payload.nameHi,
                  gu: payload.nameGu,
                },
              };
        setCats((prev) => [...prev, created]);
      }
      setFormOpen(false);
    } catch (e) {
      setSaveErr(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await categoryService.remove(delId);
      setCats((prev) => prev.filter((c) => c.id !== delId));
      setDelId(null);
    } catch (e) {
      alert("Delete failed: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "var(--serif)",
              fontSize: 26,
              color: "var(--ink)",
            }}
          >
            {t("manageCategories")}
          </h2>
          <p style={{ color: "var(--stone)", fontSize: 13, marginTop: 2 }}>
            {cats.length}{" "}
            {lang === "hi"
              ? "श्रेणियां"
              : lang === "gu"
                ? "શ્રેણીઓ"
                : "categories"}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<Icons.Plus size={14} />}
          onClick={openAdd}
        >
          {t("addCategory") || "Add Category"}
        </Button>
      </div>

      {error && <TabErr msg={error} onRetry={load} />}

      {/* Category cards — responsive */}
      {loading ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
            gap: 14,
          }}
        >
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                borderRadius: 14,
                background: "var(--foam)",
                height: 180,
                animation: "pulse 1.4s ease-in-out infinite",
              }}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
            gap: 14,
          }}
        >
          {cats.map((c) => (
            <div
              key={c.id}
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: "22px",
                border: "1px solid rgba(0,0,0,.07)",
                boxShadow: "var(--shadow-sm)",
                textAlign: "center",
                transition: "all .25s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "var(--shadow-md)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                e.currentTarget.style.transform = "";
              }}
            >
              <div
                style={{
                  fontSize: 42,
                  marginBottom: 11,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {c.imageUrl ? (
                  <img
                    src={uploadService.getUrl(c.imageUrl)}
                    alt={c.name.en}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                ) : (
                  c.icon
                )}
              </div>
              {/* Category name switches language */}
              <h3
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "var(--ink)",
                  marginBottom: 3,
                }}
              >
                {c.name[lang] || c.name.en}
              </h3>
              <p
                style={{
                  color: "var(--stone)",
                  fontSize: 12,
                  marginBottom: 14,
                }}
              >
                {c.count}{" "}
                {lang === "hi"
                  ? "उत्पाद"
                  : lang === "gu"
                    ? "ઉત્પાદન"
                    : "products"}
              </p>
              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                <button
                  onClick={() => openEdit(c)}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    background: "var(--foam)",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--pine)",
                    fontFamily: "var(--sans)",
                    transition: "all .2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--mint)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "var(--foam)")
                  }
                >
                  <Icons.Edit size={12} /> {t("update") || "Edit"}
                </button>
                <button
                  onClick={() => setDelId(c.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fee2e2",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 9px",
                    cursor: "pointer",
                    color: "#991b1b",
                    transition: "all .2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#fca5a5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fee2e2")
                  }
                >
                  <Icons.Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={
          editing?.id
            ? t("editCategory") || "Edit Category"
            : t("addCategory") || "Add Category"
        }
        footer={
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              variant="primary"
              size="md"
              loading={saving}
              style={{ flex: 1, justifyContent: "center" }}
              onClick={save}
            >
              💾 {editing?.id ? t("update") || "Update" : t("save")}
            </Button>
            <Button
              variant="ghost"
              size="md"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setFormOpen(false)}
            >
              {t("cancel")}
            </Button>
          </div>
        }
      >
        {editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {saveErr && (
              <div
                style={{
                  background: "#fee2e2",
                  border: "1px solid #fca5a5",
                  borderRadius: 8,
                  padding: "9px 13px",
                  fontSize: 13,
                  color: "#991b1b",
                }}
              >
                {saveErr}
              </div>
            )}
            <ImageUploader
              currentImage={editing.imageUrl}
              prefix="category"
              label="Category Image (optional)"
              height={140}
              onUploaded={(path) =>
                setEditing((p) => ({ ...p, imageUrl: path }))
              }
            />
            {/* Icon picker */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: "var(--stone)",
                  marginBottom: 7,
                }}
              >
                Icon (Emoji)
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 10,
                    background: "var(--foam)",
                    border: "1px solid var(--mist)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 30,
                    flexShrink: 0,
                  }}
                >
                  {editing.icon || "🌱"}
                </div>
                <input
                  className="field"
                  value={editing.icon}
                  onChange={(e) =>
                    setEditing((p) => ({ ...p, icon: e.target.value }))
                  }
                  placeholder="Paste an emoji"
                  maxLength={6}
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {EMOJI_LIST.map((em) => (
                  <button
                    key={em}
                    type="button"
                    onClick={() => setEditing((p) => ({ ...p, icon: em }))}
                    style={{
                      fontSize: 20,
                      padding: "4px 8px",
                      border: `2px solid ${editing.icon === em ? "var(--emerald)" : "var(--mist)"}`,
                      borderRadius: 8,
                      background: editing.icon === em ? "var(--foam)" : "#fff",
                      cursor: "pointer",
                      transition: "all .15s",
                    }}
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>
            <Input
              label="Name (English) *"
              name="nameEn"
              value={editing.nameEn}
              required
              onChange={(e) => {
                setEditing((p) => ({ ...p, nameEn: e.target.value }));
                if (saveErr) setSaveErr("");
              }}
              placeholder="e.g. Vegetable Seeds"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Input
                label="Name (हिंदी)"
                name="nameHi"
                value={editing.nameHi}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, nameHi: e.target.value }))
                }
                placeholder="सब्जी के बीज"
              />
              <Input
                label="Name (ગુજરાતી)"
                name="nameGu"
                value={editing.nameGu}
                onChange={(e) =>
                  setEditing((p) => ({ ...p, nameGu: e.target.value }))
                }
                placeholder="શાકભાજીના બીજ"
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={Boolean(delId)}
        onClose={() => setDelId(null)}
        title="Delete Category"
        subtitle="This action cannot be undone."
        size="sm"
        footer={
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              variant="danger"
              size="md"
              loading={deleting}
              style={{ flex: 1, justifyContent: "center" }}
              onClick={del}
            >
              {t("yesDelete") || "Yes, Delete"}
            </Button>
            <Button
              variant="ghost"
              size="md"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setDelId(null)}
            >
              {t("cancel")}
            </Button>
          </div>
        }
      >
        <p style={{ color: "var(--stone)", fontSize: 15 }}>
          Are you sure you want to delete{" "}
          <strong style={{ color: "var(--ink)" }}>
            {cats.find((c) => c.id === delId)?.name[lang] ||
              cats.find((c) => c.id === delId)?.name.en}
          </strong>
          ?
        </p>
      </Modal>
    </div>
  );
};

// ─── Inquiries Tab ────────────────────────────────────────────────────────────
const InquiriesTab = () => {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    inquiryService
      .getAll()
      .then(setList)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const markReplied = async (id) => {
    try {
      await inquiryService.markReplied(id);
      setList((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: "Replied" } : x)),
      );
    } catch (e) {
      alert("Failed: " + e.message);
    }
  };
  const remove = (id) => setList((prev) => prev.filter((x) => x.id !== id));

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2
          style={{
            fontFamily: "var(--serif)",
            fontSize: 26,
            color: "var(--ink)",
          }}
        >
          {t("manageInquiries")}
        </h2>
        <p style={{ color: "var(--stone)", fontSize: 13, marginTop: 2 }}>
          {list.filter((i) => i.status === "New").length}{" "}
          {t("newInquiry") || "new"} · {list.length} total
        </p>
      </div>

      {error && <TabErr msg={error} onRetry={load} />}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              style={{
                borderRadius: 14,
                background: "var(--foam)",
                height: 88,
                animation: "pulse 1.4s ease-in-out infinite",
              }}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {list.map((inq) => (
            <div
              key={inq.id}
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: "17px 20px",
                border: "1px solid rgba(0,0,0,.07)",
                borderLeft: `3px solid ${inq.status === "New" ? "var(--emerald)" : "transparent"}`,
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
                boxShadow: "var(--shadow-sm)",
                transition: "box-shadow .2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow = "var(--shadow-md)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = "var(--shadow-sm)")
              }
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,var(--sage),var(--pine))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--serif)",
                  fontSize: 18,
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                {inq.name.charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "var(--ink)",
                    marginBottom: 2,
                  }}
                >
                  {inq.name}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--stone)",
                    marginBottom: 3,
                  }}
                >
                  {inq.phone}
                  {inq.date ? ` · ${inq.date}` : ""}
                </div>
                <div style={{ fontSize: 14, color: "var(--stone)" }}>
                  {inq.msg}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Badge color={inq.status === "New" ? "green" : "amber"} dot>
                  {inq.status}
                </Badge>
                {inq.status === "New" && (
                  <button
                    onClick={() => markReplied(inq.id)}
                    style={{
                      background: "var(--foam)",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 13px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "var(--sans)",
                      color: "var(--pine)",
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--mint)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "var(--foam)")
                    }
                  >
                    {t("markReplied") || "Mark Replied"}
                  </button>
                )}
                <button
                  onClick={() => remove(inq.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fee2e2",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    color: "#991b1b",
                    transition: "all .2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#fca5a5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fee2e2")
                  }
                >
                  <Icons.Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Login Screen ─────────────────────────────────────────────────────────────
const LoginScreen = ({ onSuccess }) => {
  const { t } = useLang();
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const { execute, loading } = useAsync(authService.login);

  const login = async () => {
    if (!creds.username || !creds.password) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    try {
      await execute(creds);
      onSuccess();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Brand panel — hidden on mobile */}
      <div
        className="hide-sm"
        style={{
          flex: 1,
          background: "linear-gradient(160deg,var(--forest),var(--pine))",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "clamp(40px,6vw,70px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle at 30% 70%,rgba(58,158,88,.15),transparent 50%)",
          }}
        />
        <div style={{ position: "relative" }}>
          <div style={{ marginBottom: 32 }}>
            <Logo inverted />
          </div>
          <h2
            style={{
              fontFamily: "var(--serif)",
              fontSize: 34,
              color: "#fff",
              marginBottom: 12,
            }}
          >
            Admin Panel
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,.55)",
              fontSize: 15,
              lineHeight: 1.85,
              maxWidth: 320,
              fontWeight: 300,
              marginBottom: 36,
            }}
          >
            {t("manageProducts") || "Manage products"},{" "}
            {t("manageCategories") || "categories"} and customer inquiries.
          </p>
          {[
            t("manageProducts"),
            t("manageInquiries"),
            "Update website content",
            "Monitor metrics",
          ].map((f) => (
            <div
              key={f}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "rgba(255,255,255,.65)",
                fontSize: 14,
                marginBottom: 10,
              }}
            >
              <span style={{ color: "var(--mint)" }}>
                <Icons.Check size={13} />
              </span>{" "}
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Form panel */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px",
          background: "#fff",
        }}
      >
        <div style={{ width: "100%", maxWidth: 360 }}>
          <h3
            style={{
              fontFamily: "var(--serif)",
              fontSize: 26,
              color: "var(--ink)",
              marginBottom: 6,
            }}
          >
            {t("signIn") || "Sign in"}
          </h3>
          <p style={{ color: "var(--stone)", fontSize: 14, marginBottom: 28 }}>
            Enter your credentials to access the dashboard.
          </p>
          {error && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fca5a5",
                borderRadius: 10,
                padding: "9px 13px",
                fontSize: 13,
                color: "#991b1b",
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input
              label={t("username")}
              name="user"
              value={creds.username}
              onChange={(e) =>
                setCreds((p) => ({ ...p, username: e.target.value }))
              }
              placeholder="admin"
              required
            />
            <Input
              label={t("password")}
              name="pass"
              type="password"
              value={creds.password}
              onChange={(e) =>
                setCreds((p) => ({ ...p, password: e.target.value }))
              }
              placeholder="••••••••"
              required
            />
          </div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            rightIcon={<Icons.Arrow size={15} />}
            onClick={login}
            style={{ marginTop: 20 }}
          >
            {t("signIn")}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── AdminPage root ───────────────────────────────────────────────────────────
const AdminPage = ({ setPage }) => {
  const [authed, setAuthed] = useState(() => authService.isAuth());
  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;

  const renderTab = () => {
    switch (tab) {
      case "dashboard":
        return <DashboardTab onInquiries={() => setTab("inquiries")} />;
      case "products":
        return <ProductsTab />;
      case "categories":
        return <CategoriesTab />;
      case "inquiries":
        return <InquiriesTab />;
      default:
        return <DashboardTab onInquiries={() => setTab("inquiries")} />;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f7f8f7" }}>
      <Sidebar
        tab={tab}
        setTab={setTab}
        onLogout={() => {
          authService.logout();
          setAuthed(false);
        }}
        onViewSite={() => setPage("home")}
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />

      <main
        className="admin-main"
        style={{
          flex: 1,
          padding: "clamp(16px,3vw,34px)",
          overflowY: "auto",
          minHeight: "100vh",
        }}
      >
        {/* Mobile topbar — hamburger + title */}
        <div
          className="admin-hamburger"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            padding: "12px 0",
          }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 6,
              color: "var(--pine)",
              display: "flex",
            }}
          >
            <Icons.Menu size={22} />
          </button>
          <Logo />
        </div>

        {renderTab()}
      </main>
    </div>
  );
};

export default AdminPage;
